// src/data/schemas/shared.js
// Shared utilities for every schema module.
// Import: import { uuid, nowISO, runValidation, toFirestoreClean } from './shared'

// ── ID generation ─────────────────────────────────────────────────
export function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Timestamps ────────────────────────────────────────────────────
export const nowISO = () => new Date().toISOString();
export const nowMs  = () => Date.now();

// ── Type guards ───────────────────────────────────────────────────
export const isStr  = (v) => typeof v === 'string' && v.trim().length > 0;
export const isNum  = (v) => typeof v === 'number' && isFinite(v);
export const isBool = (v) => typeof v === 'boolean';
export const isLat  = (v) => isNum(v) && v >= -90   && v <= 90;
export const isLon  = (v) => isNum(v) && v >= -180  && v <= 180;
export const isISO  = (v) => typeof v === 'string' && !isNaN(Date.parse(v));
export const isIn   = (v, set) => set.includes(v);
export const isArr  = (v) => Array.isArray(v);
export const isPos  = (v) => isNum(v) && v >= 0;

// ── Schema version ────────────────────────────────────────────────
// Every Firestore document carries its schema version.
// When a schema field is renamed or a type changes, bump CURRENT_SCHEMA_VERSION
// and add a migration branch in fromFirestore().
// Rule: schemas are append-only within a version; breaking changes bump the version.
export const CURRENT_SCHEMA_VERSION = 1;

// ── Validation engine ─────────────────────────────────────────────
// Field rule shape: { required, type, check, message, maxLen, min, max }
// type: JS typeof string OR 'array'
export function runValidation(obj, schema) {
  const errors = [];

  for (const [field, rules] of Object.entries(schema)) {
    const val    = obj[field];
    const absent = val === undefined || val === null || val === '';

    if (rules.required && absent) {
      errors.push(`${field}: required`);
      continue;
    }
    if (absent) continue;

    if (rules.type) {
      const actual = Array.isArray(val) ? 'array' : typeof val;
      if (actual !== rules.type) {
        errors.push(`${field}: expected ${rules.type}, got ${actual}`);
        continue;
      }
    }
    if (rules.maxLen && typeof val === 'string' && val.length > rules.maxLen) {
      errors.push(`${field}: max ${rules.maxLen} chars (got ${val.length})`);
    }
    if (rules.min !== undefined && isNum(val) && val < rules.min) {
      errors.push(`${field}: min ${rules.min} (got ${val})`);
    }
    if (rules.max !== undefined && isNum(val) && val > rules.max) {
      errors.push(`${field}: max ${rules.max} (got ${val})`);
    }
    if (rules.check && !rules.check(val)) {
      errors.push(`${field}: ${rules.message ?? 'invalid'} (got ${JSON.stringify(val)})`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Firestore serialisation helpers ───────────────────────────────
// toFirestoreClean(obj, allowedKeys)
//   • Keeps only keys defined in the schema — never leaks _private, seed-only,
//     or computed fields to Firestore.
//   • Adds _schemaVersion so documents are self-describing.
//   • Strips keys whose value is undefined (Firestore rejects them).
export function toFirestoreClean(obj, allowedKeys) {
  const out = { _schemaVersion: CURRENT_SCHEMA_VERSION };
  for (const key of allowedKeys) {
    const val = obj[key];
    if (val !== undefined) out[key] = val;
  }
  return out;
}

// fromFirestoreNormalize(data, factory, migrations)
//   • Runs any version-specific migrations before passing data to the factory.
//   • Guarantees the returned object has all factory defaults even for old docs.
export function fromFirestoreNormalize(data, factory, migrations = {}) {
  const version = data._schemaVersion ?? 0;
  let migrated  = { ...data };

  // Apply each migration in order from document version up to current
  for (let v = version; v < CURRENT_SCHEMA_VERSION; v++) {
    if (migrations[v]) migrated = migrations[v](migrated);
  }

  // Drop the meta field — factory doesn't know about it
  delete migrated._schemaVersion;
  return factory(migrated);
}

// ── Shared enums ──────────────────────────────────────────────────
export const LOUISIANA_PARISHES = [
  'Orleans', 'Jefferson', 'St. Bernard', 'Plaquemines',
  'St. Tammany', 'St. Charles', 'St. John', 'Lafourche',
  'Terrebonne', 'Assumption', 'Ascension', 'Livingston',
];

export const APP_ZONES = [
  'N.O. East', 'Lakeview', 'Gentilly', 'Lower 9th Ward',
  'Carrollton', 'Uptown', 'Mid-City', 'French Quarter',
  'Algiers', 'Northshore / Slidell',
];

export const SEVERITY_LEVEL = [1, 2, 3, 4, 5];
// 1=informational  2=minor  3=moderate  4=critical  5=life-threatening
